import { useNavigate } from 'react-router-dom';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import EmailAddressField from 'components/fields/EmailAddressField';
import UserFormFields from 'components/forms/UserFormFields';

import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';
import useUser from 'hooks/useUser';

import useTranslation from './hooks/useTranslation';

import { EMAIL_FIELD_NAME, TEST_ID } from './constants';
import type { FunctionComponent, InviteUserFormSchema, Props } from './types';

import styles from './styles.module.scss';

const InviteUserForm: FunctionComponent<Props> = ({
    className = '',
    onCancelClose,
    onClose,
    onCreated,
    requestingClose,
    testId = TEST_ID
}) => {
    const { usersPage } = usePageRoutes();
    const { inviteUser, isLoading } = useUser();
    const { EMAIL_ADDRESS_FIELD_PLACEHOLDER, INVITE } = useTranslation();
    const navigate = useNavigate();
    const form = useForm<InviteUserFormSchema>({
        mode: 'onChange'
    });

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit: handleFormSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const handleSubmit = async (data: InviteUserFormSchema) => {
        const { email, roles } = data;

        const result = await inviteUser({ user: { email, roles: roles.map(role => role.value) } });

        navigate(usersPage);

        if (result) {
            onCreated?.();
        }
    };

    return (
        <VerticalContainer className={`${styles.inviteUserForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <UserFormFields>
                        <EmailAddressField
                            control={control}
                            isRequired={true}
                            name={EMAIL_FIELD_NAME}
                            placeholder={EMAIL_ADDRESS_FIELD_PLACEHOLDER}
                        />
                    </UserFormFields>

                    <FormSubmitPanel
                        buttonText={INVITE}
                        disabled={isLoading}
                        errors={errors}
                        onSubmit={handleFormSubmit(handleSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default InviteUserForm;
